import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../../shared/services/authService';
import { useShop } from '../../../shared/store';
import type { SupportedLanguageCode } from '../../../shared/types';

type AuthMode = 'login' | 'register' | 'forgot-password' | 'reset-password' | 'reset-password-success';
type RegisterSubmissionStatus = 'idle' | 'pending' | 'success' | 'failed';
type RegisterStep = 1 | 2 | 3;

interface LoginFormState {
  email: string;
  password: string;
}

interface RegisterFormState {
  username: string;
  firstname: string;
  lastname: string;
  telephone: string;
  email: string;
  password: string;
  // legacy fields kept for compatibility but no longer required
  phoneCode: string;
  phoneNumber: string;
  country: string;
  preferredLanguage: SupportedLanguageCode;
  dateOfBirth: string;
}

interface ForgotPasswordFormState {
  email: string;
}

interface ResetPasswordFormState {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

const extractResetTokenFromText = (rawInput: string): string => {
  const stripped = rawInput.trim().replace(/^['"]+|['"]+$/g, '').replace(/^Bearer\s+/i, '').trim();

  if (!stripped) {
    return '';
  }

  const tryDecode = (value: string) => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };

  const readUrlToken = (urlCandidate: string): string | null => {
    try {
      const url = new URL(urlCandidate);
      const possibleKeys = ['code', 'token', 'resetToken', 'reset_token', 'resetCode', 'reset_code'];
      for (const key of possibleKeys) {
        const found = url.searchParams.get(key);
        if (found) {
          return tryDecode(found).trim();
        }
      }
    } catch {
      // ignore parse errors
    }

    return null;
  };

  const maybeUrl = (() => {
    if (/^https?:\/\//i.test(stripped)) return stripped;
    if (/^localhost[:/]/i.test(stripped)) return `http://${stripped}`;
    return null;
  })();

  const urlToken = maybeUrl ? readUrlToken(maybeUrl) : null;
  if (urlToken) {
    return urlToken;
  }

  const queryTokenMatch = stripped.match(
    /(?:\?|&)(?:code|token|resetToken|reset_token|resetCode|reset_code)=([^&#\s]+)/i,
  );
  if (queryTokenMatch?.[1]) {
    return tryDecode(queryTokenMatch[1]).trim();
  }

  const jwtMatch = stripped.match(/[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/);
  if (jwtMatch?.[0]) {
    return jwtMatch[0];
  }

  const sixDigitMatch = stripped.match(/\b\d{6}\b/);
  if (sixDigitMatch?.[0]) {
    return sixDigitMatch[0];
  }

  const contextualCodeMatch = stripped.match(/code[^0-9]{0,10}(\d{4,8})/i);
  if (contextualCodeMatch?.[1]) {
    return contextualCodeMatch[1];
  }

  const afterColon = stripped.match(/:\s*([^\s]+)/);
  if (afterColon?.[1]) {
    return afterColon[1].trim();
  }

  return stripped;
};

const initialLoginState: LoginFormState = {
  email: '',
  password: '',
};

const initialRegisterState: RegisterFormState = {
  username: '',
  firstname: '',
  lastname: '',
  telephone: '',
  email: '',
  password: '',
  phoneCode: '+250',
  phoneNumber: '',
  country: 'Rwanda',
  preferredLanguage: 'en',
  dateOfBirth: '',
};

const initialForgotPasswordState: ForgotPasswordFormState = {
  email: '',
};

const initialResetPasswordState: ResetPasswordFormState = {
  token: '',
  newPassword: '',
  confirmPassword: '',
};

const stepLabels: Array<{ step: number; label: string }> = [{ step: 1, label: 'Account' }];

const formInputClassName = (isDark: boolean) =>
  `w-full rounded-xl border ${isDark ? 'border-slate-700 bg-slate-800 text-white placeholder:text-slate-400' : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400'} px-4 py-2.5 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100`;

interface ConfettiPiece {
  x: number;
  y: number;
  rotate: number;
  delay: number;
  duration: number;
  width: number;
  height: number;
  color: string;
}

const confettiColors = ['#fb923c', '#f97316', '#fbbf24', '#22c55e', '#38bdf8', '#ef4444'];

const confettiPieces: ConfettiPiece[] = Array.from({ length: 24 }, (_, index) => {
  const angle = (index / 24) * Math.PI * 2;
  const distance = 90 + (index % 6) * 18;
  const width = 4 + (index % 3) * 2;
  const height = 10 + (index % 4) * 3;

  return {
    x: Math.cos(angle) * distance,
    y: 70 + Math.abs(Math.sin(angle)) * 120 + (index % 5) * 12,
    rotate: (index % 2 === 0 ? 1 : -1) * (120 + index * 11),
    delay: (index % 8) * 0.02,
    duration: 0.9 + (index % 4) * 0.14,
    width,
    height,
    color: confettiColors[index % confettiColors.length],
  };
});

const _getAgeFromDateOfBirth = (_dateOfBirth: string): number | null => {
  if (!_dateOfBirth) {
    return null;
  }

  const parsedDate = new Date(_dateOfBirth);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - parsedDate.getFullYear();
  const monthDifference = today.getMonth() - parsedDate.getMonth();
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < parsedDate.getDate())) {
    age -= 1;
  }

  return age;
};

const AuthPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, loginBuyerAsync, loginWithSocial, isLoading, themeMode } = useShop();
  const isDark = themeMode === 'dark';
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [loginFormState, setLoginFormState] = useState<LoginFormState>(initialLoginState);
  const [registerFormState, setRegisterFormState] = useState<RegisterFormState>(initialRegisterState);
  const [forgotPasswordFormState, setForgotPasswordFormState] = useState<ForgotPasswordFormState>(initialForgotPasswordState);
  const [resetPasswordFormState, setResetPasswordFormState] = useState<ResetPasswordFormState>(initialResetPasswordState);
  const [registerStep] = useState<RegisterStep>(1);
  const [passwordResetEmail, setPasswordResetEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [formErrorList, setFormErrorList] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formSuccess, setFormSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registerSubmissionStatus, setRegisterSubmissionStatus] = useState<RegisterSubmissionStatus>('idle');
  const [showPartyPoppers, setShowPartyPoppers] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const isBusy = isSubmitting || isLoading;

  useEffect(() => {
    if (authMode === 'register' && registerSubmissionStatus === 'success') {
      return;
    }

    if (currentUser?.role === 'admin') {
      void navigate('/admin/dashboard/overview', { replace: true });
    } else if (currentUser?.role === 'manager') {
      void navigate('/admin/dashboard/pod', { replace: true });
    } else if (currentUser?.role === 'vendor') {
      void navigate('/vendor/dashboard/overview', { replace: true });
    } else if (currentUser?.role === 'buyer') {
      void navigate('/', { replace: true });
    }
  }, [authMode, currentUser, navigate, registerSubmissionStatus]);

  const clearErrorsFor = (keys: string | string[]) => {
    const targetKeys = Array.isArray(keys) ? keys : [keys];
    setFieldErrors((previous) => {
      const next = { ...previous };
      targetKeys.forEach((key) => delete next[key]);
      return next;
    });
    setFormError('');
    setFormErrorList([]);
  };

  const handleLoginFieldChange = (fieldName: keyof LoginFormState) => (event: ChangeEvent<HTMLInputElement>) => {
    setLoginFormState((previousState) => ({ ...previousState, [fieldName]: event.target.value }));
    clearErrorsFor([`login.${fieldName}`]);
  };

  const handleRegisterFieldChange = (fieldName: keyof RegisterFormState) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setRegisterFormState((previousState) => ({ ...previousState, [fieldName]: event.target.value }));
    clearErrorsFor([`register.${fieldName}`]);
  };

  const handleForgotPasswordFieldChange = (fieldName: keyof ForgotPasswordFormState) => (event: ChangeEvent<HTMLInputElement>) => {
    setForgotPasswordFormState((previousState) => ({ ...previousState, [fieldName]: event.target.value }));
  };

  const handleResetPasswordFieldChange = (fieldName: keyof ResetPasswordFormState) => (event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const nextValue =
      fieldName === 'token' ? extractResetTokenFromText(rawValue) : rawValue;

    setResetPasswordFormState((previousState) => ({ ...previousState, [fieldName]: nextValue }));
    clearErrorsFor([`reset.${fieldName}`]);
  };

  const validateLoginForm = (): string | null => {
    const normalizedEmail = loginFormState.email.trim().toLowerCase();
    const errors: Record<string, string> = {};

    if (!normalizedEmail) {
      errors['login.email'] = 'Email is required.';
    } else if (!emailPattern.test(normalizedEmail)) {
      errors['login.email'] = 'Enter a valid email address.';
    }

    if (!loginFormState.password.trim()) {
      errors['login.password'] = 'Password is required.';
    } else if (loginFormState.password.length < 6) {
      errors['login.password'] = 'Password must be at least 6 characters.';
    }

    setFieldErrors((previous) => ({ ...previous, ...errors }));
    setFormErrorList(authMode === 'register' ? Object.values(errors) : []);
    setFormError(Object.values(errors)[0] ?? '');

    return Object.keys(errors).length === 0 ? normalizedEmail : null;
  };

  const validateRegisterForm = (): boolean => {
    const errors: Record<string, string> = {};
    const requiredFields: Array<[keyof RegisterFormState, string]> = [
      ['username', 'Username is required.'],
      ['firstname', 'First name is required.'],
      ['lastname', 'Last name is required.'],
      ['telephone', 'Telephone is required.'],
      ['email', 'Email is required.'],
      ['password', 'Password is required.'],
    ];

    requiredFields.forEach(([field, message]) => {
      if (!registerFormState[field].trim()) {
        errors[`register.${field}`] = message;
      }
    });

    const normalizedEmail = registerFormState.email.trim().toLowerCase();
    if (normalizedEmail && !emailPattern.test(normalizedEmail)) {
      errors['register.email'] = 'Enter a valid email address.';
    }

    if (registerFormState.password && registerFormState.password.trim().length < 6) {
      errors['register.password'] = 'Password must be at least 6 characters.';
    }

    if (registerFormState.telephone && registerFormState.telephone.trim().length < 8) {
      errors['register.telephone'] = 'Telephone should include country code and at least 8 digits.';
    }

    if (!hasAgreedToTerms) {
      errors['register.terms'] = 'You must agree to the Terms of Use.';
    }

    setFieldErrors((previous) => ({ ...previous, ...errors }));
    setFormErrorList(Object.values(errors));
    setFormError(Object.values(errors)[0] ?? '');

    return Object.keys(errors).length === 0;
  };

  const _validateRegisterStep = (_step: RegisterStep): boolean => {
    if (_step === 1) {
      return validateRegisterForm();
    }

    return true;
  };

  const _handleNextRegisterStep = () => {
    void handleRegisterSubmit();
  };

  const _handlePreviousRegisterStep = () => {
    setFormError('');
  };

  const handleLoginSubmit = async () => {
    const normalizedEmail = validateLoginForm();
    if (!normalizedEmail) return;

    setIsSubmitting(true);
    setFormError('');
    setFormErrorList([]);

    try {
      const result = await loginBuyerAsync(normalizedEmail, loginFormState.password);
      if (result.success) {
        const loggedUser = authService.getStoredUser();
        if (loggedUser?.role === 'admin') {
          void navigate('/admin/dashboard/overview', { replace: true });
        } else if (loggedUser?.role === 'vendor') {
          void navigate('/vendor/dashboard/overview', { replace: true });
        } else if (loggedUser?.role === 'manager') {
          void navigate('/admin/dashboard/pod', { replace: true });
        } else {
          void navigate('/', { replace: true });
        }
      } else {
        setFormError(result.message || 'Login failed. Please check your credentials.');
        setFormErrorList([]);
      }
    } catch (error) {
      setFormError('An error occurred during login. Please try again.');
      setFormErrorList([]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegisterSubmit = async () => {
    if (!_validateRegisterStep(1)) {
      setRegisterSubmissionStatus('failed');
      return;
    }

    setIsSubmitting(true);
    setFormError('');
    setFormErrorList([]);
    setFormSuccess('');
    setRegisterSubmissionStatus('pending');
    setShowPartyPoppers(false);

    try {
      const signupResult = await authService.signup({
        username: registerFormState.username.trim(),
        firstname: registerFormState.firstname.trim(),
        lastname: registerFormState.lastname.trim(),
        telephone: registerFormState.telephone.trim(),
        email: registerFormState.email.trim().toLowerCase(),
        password: registerFormState.password,
      });

      if (signupResult.success) {
        // auto-login after successful signup for smoother UX
        const loginResult = await loginBuyerAsync(registerFormState.email, registerFormState.password);
        setFormSuccess('Registration successful. Welcome to haapa!');
        setRegisterSubmissionStatus('success');
        setShowPartyPoppers(true);
        window.setTimeout(() => {
          setShowPartyPoppers(false);
          if (loginResult.success) {
            void navigate('/', { replace: true });
          } else {
            setAuthMode('login');
          }
        }, 1400);
      } else {
        setFormError(signupResult.message || 'Registration failed. Please try again.');
        setFormErrorList(signupResult.message ? [signupResult.message] : []);
        setRegisterSubmissionStatus('failed');
      }
    } catch (error) {
      const message =
        typeof (error as { message?: unknown } | null)?.message === 'string'
          ? String((error as { message: string }).message)
          : 'An error occurred during registration. Please try again.';
      setFormError(message);
      setFormErrorList([message]);
      setRegisterSubmissionStatus('failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPasswordSubmit = async () => {
    const normalizedEmail = forgotPasswordFormState.email.trim().toLowerCase();

    if (!normalizedEmail) {
      setFormError('Please enter your email address.');
      return;
    }

    setIsSubmitting(true);
    setFormError('');
    setFormSuccess('');

    try {
      const result = await authService.forgotPassword({ email: normalizedEmail });
      if (result.success) {
        setPasswordResetEmail(normalizedEmail);
        setResetPasswordFormState(initialResetPasswordState);
        setAuthMode('reset-password');
        setFormSuccess(
          result.message ||
            `Reset code sent to ${normalizedEmail}. Enter the code and your new password below.`,
        );
        setForgotPasswordFormState({ email: normalizedEmail });
      } else {
        setFormError(result.message || 'Failed to send reset code. Please try again.');
      }
    } catch (error) {
      setFormError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPasswordSubmit = async () => {
    const resetCode = resetPasswordFormState.token.trim();
    if (!resetCode) {
      setFormError('Please enter the reset code.');
      return;
    }

    if (!resetPasswordFormState.newPassword.trim()) {
      setFormError('Please enter a new password.');
      return;
    }

    if (resetPasswordFormState.newPassword.length < 6) {
      setFormError('Password must be at least 6 characters.');
      return;
    }

    if (resetPasswordFormState.newPassword !== resetPasswordFormState.confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    setFormError('');
    setFormSuccess('');

    try {
      const emailContext =
        passwordResetEmail ||
        forgotPasswordFormState.email.trim().toLowerCase();
      const result = await authService.resetPassword({
        token: resetCode,
        code: resetCode,
        newPassword: resetPasswordFormState.newPassword,
        email: emailContext || undefined,
      });

      if (result.success) {
        setFormSuccess(
          emailContext
            ? `Password reset successful for ${emailContext}. Please login with your new password.`
            : 'Password reset successful! Please login with your new password.',
        );
        setAuthMode('reset-password-success');
        setResetPasswordFormState(initialResetPasswordState);
      } else {
        setFormError(result.message || 'Failed to reset password. Please check your code.');
      }
    } catch (error) {
      setFormError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (authMode === 'login') {
      handleLoginSubmit();
      return;
    }

    if (authMode === 'register') {
      handleRegisterSubmit();
      return;
    }

    if (authMode === 'forgot-password') {
      handleForgotPasswordSubmit();
      return;
    }

    if (authMode === 'reset-password') {
      handleResetPasswordSubmit();
      return;
    }

    if (authMode === 'reset-password-success') {
      setAuthMode('login');
      setPasswordResetEmail('');
      setForgotPasswordFormState(initialForgotPasswordState);
      setResetPasswordFormState(initialResetPasswordState);
      setFormError('');
      setFormSuccess('');
    }
  };

  const authImageSrc =
    authMode === 'login'
      ? '/kids/2cc8c2f37f235aa54af11ab2d12dbaa0.jpg'
      : '/kids/49dee260adaeb4a3ba59128f474f9f07.jpg';

  const authShellClass = isDark
    ? 'bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900'
    : 'bg-[linear-gradient(180deg,#fff4e8_0%,#fffaf4_34%,#ffffff_100%)]';
  const authFrameClass = isDark
    ? 'border-slate-700 bg-slate-900'
    : 'border-orange-100/80 bg-[linear-gradient(135deg,#fffdfb_0%,#fff6ee_48%,#ffffff_100%)]';
  const promoPanelClass = isDark
    ? 'border-slate-700 bg-slate-800'
    : 'border-orange-100/80 bg-[linear-gradient(180deg,#fff3e4_0%,#fffaf2_100%)]';
  const formPanelClass = isDark
    ? 'border-slate-700 bg-slate-800'
    : 'border-orange-100/80 bg-[linear-gradient(180deg,#fffaf5_0%,#ffffff_100%)]';
  const dividerClass = isDark ? 'border-slate-700' : 'border-orange-100';
  const mutedTextClass = isDark ? 'text-slate-300' : 'text-slate-600';
  const labelClass = isDark ? 'text-slate-300' : 'text-slate-700';
  const heroGlowPrimaryClass = isDark ? 'bg-orange-100/10' : 'bg-orange-100/80';
  const heroGlowSecondaryClass = isDark ? 'bg-orange-300/10' : 'bg-orange-200/40';

  return (
    <section className={`relative overflow-x-hidden ${authShellClass}`}>
      <div className={`pointer-events-none absolute inset-0 bg-[url('/wallpaper for homepage 2.png')] bg-cover bg-center ${isDark ? 'opacity-[0.1]' : 'opacity-[0.08]'}`} />
      <div className="relative mx-auto flex min-h-[calc(100vh-8rem)] w-full max-w-7xl items-center px-4 py-4 sm:px-6 lg:px-8">
        <div className={`grid w-full gap-5 rounded-[2rem] border ${authFrameClass} p-4 shadow-[0_35px_95px_-70px_rgba(249,115,22,0.6)] lg:h-[calc(100vh-9.5rem)] lg:grid-cols-2 lg:p-5`}>
          <article className={`relative space-y-3 overflow-hidden rounded-[1.5rem] border ${promoPanelClass} p-4 sm:p-5`}>
            <div className={`pointer-events-none absolute -right-14 -top-14 h-40 w-40 rounded-full blur-3xl ${heroGlowPrimaryClass}`} />
            <div className={`pointer-events-none absolute -left-8 bottom-2 h-32 w-32 rounded-full blur-2xl ${heroGlowSecondaryClass}`} />
            <p className={`inline-flex rounded-full ${isDark ? 'bg-slate-700 text-orange-400' : 'bg-white text-orange-700'} px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em]`}>
              Free Comic Book
            </p>
            <h2 className={`max-w-xl text-3xl font-semibold leading-tight ${isDark ? 'text-white' : 'text-slate-900'} md:text-4xl`}>
              {authMode === 'login' ? 'Sign in to unlock African animation stories' : 
               authMode === 'forgot-password' ? 'Reset your password' :
               authMode === 'reset-password' ? 'Create new password' :
               authMode === 'reset-password-success' ? 'Password reset complete' :
               'Create your account to join haapa stories'}
            </h2>
            <p className={`max-w-lg text-sm ${mutedTextClass} sm:text-base`}>
              {authMode === 'login'
                ? 'Join now to access featured stories inspired by Rwanda and other African cultures.'
                : authMode === 'forgot-password'
                ? 'Enter your email to receive a password reset code.'
                : authMode === 'reset-password'
                ? 'Enter the code from your email and create a new password.'
                : authMode === 'reset-password-success'
                ? 'Your password has been updated. Continue to login with the new password.'
                : 'Create your profile in quick steps with phone, country, language, and photo.'}
            </p>
            <div className={`rounded-2xl border ${isDark ? 'border-slate-700 bg-slate-900' : 'border-orange-100/80 bg-white/95 shadow-[0_18px_45px_-35px_rgba(249,115,22,0.45)]'} p-1.5`}>
              <img
                src={authImageSrc}
                alt={t('auth.title')}
                className="h-[230px] w-full rounded-xl object-cover sm:h-[280px] lg:h-[32vh]"
              />
            </div>
          </article>

          <article className={`relative rounded-[1.5rem] border ${formPanelClass} p-4 sm:p-5 lg:flex lg:flex-col lg:overflow-y-auto`}>
            {showPartyPoppers && authMode === 'register' && (
              <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden">
                <div className="absolute left-1/2 top-14 -translate-x-1/2">
                  {confettiPieces.map((piece, index) => (
                    <motion.span
                      key={`confetti-paper-${index}`}
                      className="absolute rounded-[1px]"
                      style={{
                        width: `${piece.width}px`,
                        height: `${piece.height}px`,
                        backgroundColor: piece.color,
                      }}
                      initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 0.8 }}
                      animate={{
                        x: piece.x,
                        y: piece.y,
                        opacity: 0,
                        rotate: piece.rotate,
                        scale: 1,
                      }}
                      transition={{
                        duration: piece.duration,
                        delay: piece.delay,
                        ease: 'easeOut',
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className={`inline-flex rounded-xl ${isDark ? 'bg-slate-700' : 'bg-orange-50 shadow-inner shadow-orange-100'} p-1`}>
                {authMode !== 'forgot-password' &&
                  authMode !== 'reset-password' &&
                  authMode !== 'reset-password-success' && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setAuthMode('login');
                        setPasswordResetEmail('');
                        setResetPasswordFormState(initialResetPasswordState);
                        setForgotPasswordFormState(initialForgotPasswordState);
                        setFormError('');
                        setFormSuccess('');
                        setRegisterSubmissionStatus('idle');
                        setShowPartyPoppers(false);
                      }}
                      className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                        authMode === 'login' ? 'bg-orange-500 text-white shadow-sm' : isDark ? 'text-slate-300 hover:text-orange-400' : 'text-slate-700 hover:text-orange-700'
                      }`}
                    >
                      {t('auth.loginTab')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setAuthMode('register');
                        setPasswordResetEmail('');
                        setResetPasswordFormState(initialResetPasswordState);
                        setForgotPasswordFormState(initialForgotPasswordState);
                        setFormError('');
                        setFormSuccess('');
                        setRegisterSubmissionStatus('idle');
                        setShowPartyPoppers(false);
                      }}
                      className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                        authMode === 'register' ? 'bg-orange-500 text-white shadow-sm' : isDark ? 'text-slate-300 hover:text-orange-400' : 'text-slate-700 hover:text-orange-700'
                      }`}
                    >
                      {t('auth.registerTab')}
                    </button>
                  </>
                )}
                {(authMode === 'forgot-password' ||
                  authMode === 'reset-password' ||
                  authMode === 'reset-password-success') && (
                  <button
                    type="button"
                    onClick={() => {
                      setAuthMode('login');
                      setPasswordResetEmail('');
                      setResetPasswordFormState(initialResetPasswordState);
                      setForgotPasswordFormState(initialForgotPasswordState);
                      setFormError('');
                      setFormSuccess('');
                      setRegisterSubmissionStatus('idle');
                      setShowPartyPoppers(false);
                    }}
                    className="rounded-lg px-4 py-2 text-sm font-semibold bg-orange-500 text-white shadow-sm"
                  >
                    Back to Login
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3">
                <img
                  src="/kids/haapa-logo.svg"
                  alt={t('common.brand')}
                  className="h-9 w-9 rounded-lg object-contain"
                />
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-orange-600">{t('common.brand')}</p>
              </div>
            </div>

            <h1 className={`text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'} sm:text-3xl`}>
              {authMode === 'login' ? t('auth.loginTab') : 
               authMode === 'register' ? t('auth.registerTab') :
               authMode === 'forgot-password' ? 'Forgot Password' :
               authMode === 'reset-password' ? 'Reset Password' :
               'Password Reset Complete'}
            </h1>



{authMode === 'register' && registerSubmissionStatus !== 'idle' && (
              <div
                className={`mt-3 rounded-xl border px-4 py-3 text-sm font-semibold ${
                  registerSubmissionStatus === 'pending'
                    ? 'border-amber-200 bg-amber-50 text-amber-700'
                    : registerSubmissionStatus === 'success'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-red-200 bg-red-50 text-red-600'
                }`}
              >
                {registerSubmissionStatus === 'pending' && 'Registration pending... creating your haapa account.'}
                {registerSubmissionStatus === 'success' && 'Registration successful. Redirecting you now...'}
                {registerSubmissionStatus === 'failed' && 'Registration failed. Please review your details and try again.'}
              </div>
            )}

            <form className="mt-6 grid gap-4" onSubmit={handleSubmit} noValidate>
              {authMode !== 'login' && formErrorList.length > 0 && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <ul className="list-disc space-y-1 pl-4">
                    {formErrorList.map((message, index) => (
                      <li key={`auth-error-${index}`}>{message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {authMode === 'login' && (
                <>
                  <label className={`grid gap-1 text-sm font-medium ${labelClass}`} htmlFor="auth-email">
                    Email
                    <input
                      id="auth-email"
                      type="email"
                      value={loginFormState.email}
                      onChange={handleLoginFieldChange('email')}
                      className={formInputClassName(isDark)}
                      placeholder="Enter your email"
                      autoComplete="email"
                    />
                    {fieldErrors['login.email'] && (
                      <span className="text-xs font-medium text-red-600">{fieldErrors['login.email']}</span>
                    )}
                  </label>

                  <label className={`grid gap-1 text-sm font-medium ${labelClass}`} htmlFor="auth-password">
                    Password
                    <div className="relative">
                      <input
                        id="auth-password"
                        type={showPassword ? 'text' : 'password'}
                        value={loginFormState.password}
                        onChange={handleLoginFieldChange('password')}
                        className={`${formInputClassName(isDark)} pr-10`}
                        autoComplete="current-password"
                      />
                      {fieldErrors['login.password'] && (
                        <span className="absolute left-0 -bottom-6 text-xs font-medium text-red-600">
                          {fieldErrors['login.password']}
                        </span>
                      )}
                      <button
                        type="button"
                        className={`absolute inset-y-0 right-0 flex items-center pr-3 ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'}`}
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </label>

                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setAuthMode('forgot-password');
                        setPasswordResetEmail('');
                        setResetPasswordFormState(initialResetPasswordState);
                        setForgotPasswordFormState({
                          email: loginFormState.email.trim().toLowerCase(),
                        });
                        setFormError('');
                        setFormSuccess('');
                        setRegisterSubmissionStatus('idle');
                        setShowPartyPoppers(false);
                      }}
                      className="text-sm font-semibold text-orange-600 hover:text-orange-700"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </>
              )}

              {authMode === 'register' && (
                <>
                  <label className={`grid gap-1 text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`} htmlFor="register-name">
                    Username
                    <input
                      id="register-name"
                      type="text"
                      value={registerFormState.username}
                      onChange={handleRegisterFieldChange('username')}
                      className={formInputClassName(isDark)}
                      placeholder="Enter your preferred username"
                      autoComplete="username"
                    />
                  </label>

                  <label className={`grid gap-1 text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`} htmlFor="register-firstname">
                    Firstname
                    <input
                      id="register-firstname"
                      type="text"
                      value={registerFormState.firstname}
                      onChange={handleRegisterFieldChange('firstname')}
                      className={formInputClassName(isDark)}
                      placeholder="Enter your firstname..."
                      autoComplete="given-name"
                    />
                  </label>

                  <label className={`grid gap-1 text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`} htmlFor="register-lastname">
                    Lastname
                    <input
                      id="register-lastname"
                      type="text"
                      value={registerFormState.lastname}
                      onChange={handleRegisterFieldChange('lastname')}
                      className={formInputClassName(isDark)}
                      placeholder="Enter your lastname..."
                      autoComplete="family-name"
                    />
                  </label>

                  <label className={`grid gap-1 text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`} htmlFor="register-telephone">
                    Telephone
                    <input
                      id="register-telephone"
                      type="tel"
                      value={registerFormState.telephone}
                      onChange={handleRegisterFieldChange('telephone')}
                      className={formInputClassName(isDark)}
                      placeholder="Enter your telephone..."
                      autoComplete="tel"
                    />
                  </label>

                  <label className={`grid gap-1 text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`} htmlFor="register-email">
                    Email
                    <input
                      id="register-email"
                      type="email"
                      value={registerFormState.email}
                      onChange={handleRegisterFieldChange('email')}
                      className={formInputClassName(isDark)}
                      placeholder="name@example.com"
                      autoComplete="email"
                    />
                  </label>

                  <label className={`grid gap-1 text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`} htmlFor="register-password">
                    Password
                    <input
                      id="register-password"
                      type="password"
                      value={registerFormState.password}
                      onChange={handleRegisterFieldChange('password')}
                      className={formInputClassName(isDark)}
                      placeholder="Minimum 6 characters"
                      autoComplete="new-password"
                    />
                  </label>

                  <label className={`flex items-start gap-3 text-sm font-medium ${labelClass}`}>
                    <input
                      type="checkbox"
                      className={`mt-1 h-4 w-4 rounded ${isDark ? 'border-slate-600 bg-slate-900' : 'border-slate-300 bg-white'} text-orange-500 focus:ring-2 focus:ring-orange-200`}
                      checked={hasAgreedToTerms}
                      onChange={(event) => setHasAgreedToTerms(event.target.checked)}
                    />
                    <span className={labelClass}>
                      I agree to the{' '}
                      <button
                        type="button"
                        className="font-semibold text-orange-600 underline hover:text-orange-700"
                        onClick={() => setShowTermsModal(true)}
                      >
                        Terms of Use
                      </button>
                      .
                      {fieldErrors['register.terms'] && (
                        <span className="mt-1 block text-xs font-semibold text-red-600">
                          {fieldErrors['register.terms']}
                        </span>
                      )}
                    </span>
                  </label>
                </>
              )}

              {authMode === 'forgot-password' && (
                <>
                  <label className={`grid gap-1 text-sm font-medium ${labelClass}`} htmlFor="forgot-email">
                    Email Address
                    <input
                      id="forgot-email"
                      type="email"
                      value={forgotPasswordFormState.email}
                      onChange={handleForgotPasswordFieldChange('email')}
                      className={formInputClassName(isDark)}
                      placeholder="Enter your email"
                      autoComplete="email"
                    />
                  </label>
                  <p className={`text-xs ${mutedTextClass}`}>
                    We’ll email you a short reset code (or a secure reset link) to this address.
                  </p>
                </>
              )}

              {authMode === 'reset-password' && (
                <>
                  <label className={`grid gap-1 text-sm font-medium ${labelClass}`} htmlFor="reset-token">
                    Reset Code (or Link)
                    <input
                      id="reset-token"
                      type="text"
                      value={resetPasswordFormState.token}
                      onChange={handleResetPasswordFieldChange('token')}
                      className={formInputClassName(isDark)}
                      placeholder="Paste the code, token, or full reset link"
                      autoComplete="one-time-code"
                    />
                  </label>
                  <p className={`text-xs ${mutedTextClass}`}>
                    Tip: if your email contains a long token or link, you can paste it here — we’ll extract the right code.
                  </p>

                  <label className={`grid gap-1 text-sm font-medium ${labelClass}`} htmlFor="reset-new-password">
                    New Password
                    <input
                      id="reset-new-password"
                      type="password"
                      value={resetPasswordFormState.newPassword}
                      onChange={handleResetPasswordFieldChange('newPassword')}
                      className={formInputClassName(isDark)}
                      placeholder="Enter new password"
                      autoComplete="new-password"
                    />
                  </label>

                  <label className={`grid gap-1 text-sm font-medium ${labelClass}`} htmlFor="reset-confirm-password">
                    Confirm Password
                    <input
                      id="reset-confirm-password"
                      type="password"
                      value={resetPasswordFormState.confirmPassword}
                      onChange={handleResetPasswordFieldChange('confirmPassword')}
                      className={formInputClassName(isDark)}
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                    />
                  </label>
                </>
              )}

              {authMode === 'reset-password-success' && (
                <article className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  <p className="font-semibold">Password reset successful.</p>
                  <p className="mt-1">
                    {passwordResetEmail
                      ? `The password for ${passwordResetEmail} was updated.`
                      : 'Your account password was updated.'}
                  </p>
                  <p className="mt-1 text-emerald-700">Click the button below to go back to login.</p>
                </article>
              )}

              {formError && (
                <p role="alert" className="text-sm font-semibold text-red-600">
                  {formError}
                </p>
              )}

              {formSuccess && (
                <p role="status" className="text-sm font-semibold text-green-600">
                  {formSuccess}
                </p>
              )}

              <div className="flex flex-wrap items-center justify-end gap-2">
                <button
                  type="submit"
                  disabled={isBusy}
                  className="rounded-2xl bg-orange-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isBusy ? 'Please wait...' : 
                   authMode === 'login' ? t('auth.submitLogin') :
                   authMode === 'register' ? 'Create Account' :
                   authMode === 'forgot-password' ? 'Send Reset Code' :
                   authMode === 'reset-password' ? 'Reset Password' :
                   'Back to Login'}
                </button>
              </div>
            </form>

            {(authMode === 'login' || authMode === 'register') && (
              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className={`w-full border-t ${dividerClass}`} />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className={`px-2 text-slate-500 ${isDark ? 'bg-slate-800' : 'bg-[#fffaf5]'}`}>Or continue with</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => loginWithSocial()}
                  disabled={isBusy}
                  className={`mt-4 flex w-full items-center justify-center gap-3 rounded-xl border px-4 py-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                    isDark
                      ? 'border-slate-600 bg-slate-900 text-slate-200 hover:border-slate-500 hover:bg-slate-950'
                      : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50'
                  }`}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {authMode === 'login' ? 'Sign in with Google' : 'Sign up with Google'}
                </button>
              </div>
            )}
          </article>
        </div>
      </div>

      {showTermsModal && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center px-4 ${isDark ? 'bg-slate-950/75' : 'bg-slate-900/50 backdrop-blur-sm'}`}>
          <div className={`w-full max-w-4xl overflow-hidden rounded-2xl shadow-2xl ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
            <div className={`flex items-center justify-between border-b px-4 py-3 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Terms of Use</h2>
              <button
                type="button"
                className={`rounded-full p-2 ${isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'}`}
                aria-label="Close terms modal"
                onClick={() => setShowTermsModal(false)}
              >
                ✕
              </button>
            </div>
            <div className={`h-[60vh] w-full overflow-y-auto p-5 text-sm ${isDark ? 'bg-slate-950 text-slate-300' : 'bg-slate-50 text-slate-700'}`}>
              <h3 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Terms and Conditions of Use</h3>
              <p className="mt-2">
                This website assists writers, poets, publishers, and producers of audio stories in local languages to share,
                exchange, and trade with one another and with consumers. By visiting this website, you acknowledge that we gather
                listening and transaction-related information to support lawful service delivery with audio and content suppliers.
              </p>
              <p className={`mt-2 font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Content ownership and responsibility</p>
              <p>
                By uploading, sharing, or selling audio content on this website, you confirm you have all necessary rights and you accept
                full responsibility for your content. The platform and its owners are not liable for user-generated content shared or
                consumed through this service.
              </p>
              <p className={`mt-2 font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Acceptance of terms</p>
              <p>
                By accessing or using this website, or listening to or using any product/service on it, you agree to these Terms and
                Conditions of Use. If you do not agree, please do not use this website or any audio content provided through it.
              </p>
              <p className={`mt-2 font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Use of the website</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                <li>You are at least 18 years old and of sound mind, or have legal guardian consent if under 18.</li>
                <li>You will use this website in accordance with these Terms.</li>
                <li>You will only use this website for legitimate listening, sharing, or transactions for yourself or someone you can legally represent.</li>
                <li>You will inform any represented person of all applicable rules and restrictions.</li>
                <li>All information you submit is accurate, current, and complete.</li>
                <li>If you have an account, you will protect your credentials and accept full responsibility for all activity.</li>
              </ul>
              <p className="mt-1">
                We may deny access to this website or services at any time without notice where these Terms are violated.
              </p>
              <p className={`mt-2 font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Purchase</p>
              <p>For paid content added by third-party publishers on the HAAPA platform, HAAPA retains 30% of the retail price. For free content, HAAPA retains 0%.</p>
              <p className={`mt-2 font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Refund</p>
              <p>
                Refund policy is determined by suppliers (writers, poets, publishers, and producers) who publish on HAAPA web/apps.
                Please review each product's listening/sharing policy before payment. Contact support any time for clarification.
              </p>
              <p className={`mt-2 font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Prohibited activities</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                <li>Using this website for terrorism, homophobia, or human-rights violations.</li>
                <li>Making speculative, false, or fraudulent audio publication.</li>
                <li>Accessing, monitoring, or copying content without written permission.</li>
                <li>Taking actions that impose unreasonable load on platform infrastructure.</li>
                <li>Deep-linking any portion of this website without express written permission.</li>
                <li>Embedding or incorporating any part of this website into another site without prior written authorization.</li>
                <li>
                  Modifying, copying, distributing, transmitting, publishing, licensing, creating derivative works from,
                  transferring, selling, or reselling data/software/products/services obtained through this website, except limited
                  personal copies of your own lawful routines/documents.
                </li>
              </ul>
              <p className={`mt-2 font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Fraud and account closure</p>
              <p>
                If your account shows fraud, abuse, or suspicious activity, we may cancel activity linked to your name, email, or
                account and close associated accounts. We reserve the right to take legal action for fraudulent activity, including
                claims for monetary losses, litigation costs, and damages. To contest account closure or cancellation, contact customer
                support.
              </p>
            </div>
            <div className={`flex items-center justify-end gap-3 border-t px-4 py-3 ${isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'}`}>
              <button
                type="button"
                className={`rounded-lg px-4 py-2 text-sm font-semibold ${isDark ? 'text-slate-200 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'}`}
                onClick={() => setShowTermsModal(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600"
                onClick={() => {
                  setHasAgreedToTerms(true);
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next['register.terms'];
                    return next;
                  });
                  setShowTermsModal(false);
                }}
              >
                Accept & Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AuthPage;
